<template>
  <DistrictTownView
    :district-towns="districtTowns"
    :loading="loading"
    :error="error"
    :show-modal="showModal"
    :show-delete-modal="showDeleteModal"
    :is-edit-mode="isEditMode"
    :form-data="formData"
    :form-errors="formErrors"
    :delete-target="deleteTarget"
    @show-add-modal="showAddModal"
    @show-edit-modal="showEditModal"
    @confirm-delete="confirmDelete"
    @add-town="handleAddTown"
    @edit-town="handleEditTown"
    @delete-town="handleDeleteTown"
    @close-modal="closeModal"
    @close-delete-modal="closeDeleteModal"
    @back-to-menu="handleBackToMenu"
    @refresh="handleRefresh"
    @table-action="handleTableAction"
    @submit="handleSubmit"
    @update:form-data="updateFormData"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DistrictTownView from '../components/districtTown/DistrictTownView.vue'
import {
  getDistrictTowns,
  createDistrictTown,
  updateDistrictTown,
  deleteDistrictTown
} from '@/services/systemSettings/districtTownService.js'

const emit = defineEmits(['show-success', 'back-to-menu'])

const districtTowns = ref([])
const loading = ref(false)
const error = ref(null)

const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditMode = ref(false)

const formData = ref({
  districtTownName: ''
})
const formErrors = ref({})
const deleteTarget = ref(null)

const resetForm = () => {
  formData.value = {
    districtTownName: ''
  }
  formErrors.value = {}
}

const updateFormData = (val) => {
  formData.value = val
}

const showAddModal = () => {
  isEditMode.value = false
  resetForm()
  showModal.value = true
}

const showEditModal = (town) => {
  isEditMode.value = true
  formData.value = { ...town }
  formErrors.value = {}
  showModal.value = true
}

const confirmDelete = (town) => {
  deleteTarget.value = town
  showDeleteModal.value = true
}

const closeModal = () => {
  resetForm()
  showModal.value = false
}

const closeDeleteModal = () => {
  deleteTarget.value = null
  showDeleteModal.value = false
}

/**
 * テーブルアクションの統合ハンドラー
 */
const handleTableAction = (payload) => {
  const { action, data } = payload
  switch (action) {
    case 'edit':
      showEditModal(data)
      break
    case 'delete':
      confirmDelete(data)
      break
    default:
      console.warn('Unknown table action:', action)
  }
}

/**
 * フォーム送信処理（バリデーションをContainerで実施）
 */
const handleSubmit = () => {
  const errors = {}

  if (!formData.value.districtTownName?.trim()) {
    errors.districtTownName = '地区町名は必須です'
  }

  const existingTown = districtTowns.value.find(town =>
    town.districtTownName === formData.value.districtTownName?.trim() &&
    (!isEditMode.value || town.id !== formData.value.id)
  )
  if (existingTown) {
    errors.districtTownName = '同じ地区町名が既に登録されています'
  }

  if (Object.keys(errors).length > 0) {
    formErrors.value = errors
    return
  }

  const townData = {
    ...formData.value,
    districtTownName: formData.value.districtTownName?.trim()
  }

  if (isEditMode.value) {
    handleEditTown(townData)
  } else {
    handleAddTown(townData)
  }
}

const loadDistrictTowns = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await getDistrictTowns()

    if (response.success) {
      districtTowns.value = response.data
    } else {
      error.value = response.message || '地区町名データの取得に失敗しました'
    }
  } catch (err) {
    console.error('Failed to load district towns:', err)
    error.value = 'データの取得中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleAddTown = async (townData) => {
  loading.value = true
  error.value = null

  try {
    const response = await createDistrictTown(townData)

    if (response.success) {
      districtTowns.value = response.data
      closeModal()
      emit('show-success', response.message || '地区町名が正常に追加されました')
    } else {
      error.value = response.message || '地区町名の追加に失敗しました'
    }
  } catch (err) {
    console.error('Failed to add town:', err)
    error.value = '地区町名の追加中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleEditTown = async (townData) => {
  loading.value = true
  error.value = null

  try {
    const response = await updateDistrictTown(townData.id, townData)

    if (response.success) {
      districtTowns.value = response.data
      closeModal()
      emit('show-success', response.message || '地区町名が正常に更新されました')
    } else {
      error.value = response.message || '地区町名の更新に失敗しました'
    }
  } catch (err) {
    console.error('Failed to update town:', err)
    error.value = '地区町名の更新中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleDeleteTown = async (town) => {
  loading.value = true
  error.value = null

  try {
    const response = await deleteDistrictTown(town.id)

    if (response.success) {
      districtTowns.value = response.data
      closeDeleteModal()
      emit('show-success', response.message || '地区町名が正常に削除されました')
    } else {
      error.value = response.message || '地区町名の削除に失敗しました'
    }
  } catch (err) {
    console.error('Failed to delete town:', err)
    error.value = '地区町名の削除中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleBackToMenu = () => {
  emit('back-to-menu')
}

const handleRefresh = async () => {
  await loadDistrictTowns()
}

const loadInitialData = async () => {
  await loadDistrictTowns()
}

onMounted(async () => {
  await loadInitialData()
})
</script>
